/**
 * Sandbox Export Functions
 * 
 * Two export modes:
 * 1. Sandbox (.gbox) - Single file that can be imported back into the sandbox
 * 2. Standalone (zip) - Complete project with package.json for local development
 */

import JSZip from 'jszip';
import type { SandboxSystemsExport } from '../templates/types';
import type { StoredAsset } from '../assets/types';
import { base64ToBytes } from '../assets/assetStore';
import { pinGottiRelease, toSavedProject, toV1View, type ProjectTree } from '@gotti/sandbox-core';
import {
  GBOX_FORMAT_VERSION,
  GBOX_EXTENSION,
  type GboxDocument,
  type GboxFiles,
  type GboxMeta,
  type GboxConfig,
  type GboxClient,
} from './gboxFormat';
import { writeGottiRunbooks, type GboxRunbook } from '@gotti/sandbox-shared';

/** Current sandbox version for metadata */
const SANDBOX_VERSION = '0.1.0';

/**
 * Convert SandboxSystemsExport to GboxFiles format
 * This conversion is straightforward since the structures mirror each other
 */
function systemsToGboxFiles(systems: SandboxSystemsExport): GboxFiles {
  const convertFolder = (folder: { rootFiles: Array<{ name: string; code: string }>; subfolders: Record<string, Array<{ name: string; code: string }>> }) => ({
    rootFiles: folder.rootFiles.map(f => ({ name: f.name, code: f.code })),
    subfolders: Object.fromEntries(
      Object.entries(folder.subfolders).map(([key, files]) => [
        key,
        files.map(f => ({ name: f.name, code: f.code }))
      ])
    ),
  });

  return {
    client: convertFolder(systems.client),
    server: convertFolder(systems.server),
    shared: convertFolder(systems.shared),
  };
}

/** A document's v1 `files`: the project's v1 view, for readers that know only `files`. */
const v1FilesOf = (project: ProjectTree): GboxFiles =>
  systemsToGboxFiles(toV1View(project.files, project.generated, project.folders) as SandboxSystemsExport);

/**
 * Create a GboxDocument from the editor's project (without downloading): its tree as the `project` section, and its v1
 * view as `files` for readers that know only `files`. Used for saving to backend.
 */
export function createGboxDocument(
  project: ProjectTree,
  options?: {
    name?: string;
    description?: string;
    author?: string;
    config?: GboxConfig;
    clients?: GboxClient[];
    extensions?: Record<string, unknown>;
    assets?: StoredAsset[];
  }
): GboxDocument {
  const name = options?.name || 'Untitled Sandbox';
  
  const meta: GboxMeta = {
    name,
    description: options?.description,
    author: options?.author,
    exportedAt: new Date().toISOString(),
    sandboxVersion: SANDBOX_VERSION,
  };

  return {
    version: GBOX_FORMAT_VERSION,
    meta,
    files: v1FilesOf(project),
    project: toSavedProject(project),
    config: options?.config,
    clients: options?.clients,
    extensions: options?.assets?.length
      ? { ...(options?.extensions ?? {}), assetsInline: options.assets }
      : options?.extensions,
  };
}

/**
 * Export sandbox as a .gbox file (single file format for re-importing)
 */
export async function exportAsGbox(
  project: ProjectTree,
  options?: {
    name?: string;
    description?: string;
    author?: string;
    config?: GboxConfig;
    clients?: GboxClient[];
    extensions?: Record<string, unknown>;
    assets?: StoredAsset[];
    runbooks?: GboxRunbook[];
  }
): Promise<void> {
  const name = options?.name || 'Untitled Sandbox';

  const meta: GboxMeta = {
    name,
    description: options?.description,
    author: options?.author,
    exportedAt: new Date().toISOString(),
    sandboxVersion: SANDBOX_VERSION,
  };

  let gboxDoc: GboxDocument = {
    version: GBOX_FORMAT_VERSION,
    meta,
    files: v1FilesOf(project),
    project: toSavedProject(project),
    config: options?.config,
    clients: options?.clients,
    extensions: options?.assets?.length
      ? { ...(options?.extensions ?? {}), assetsInline: options.assets }
      : options?.extensions,
  };
  if (options?.runbooks?.length) {
    gboxDoc = writeGottiRunbooks(gboxDoc, options.runbooks);
  }

  // Pretty-print for readability (still reasonably compact)
  const content = JSON.stringify(gboxDoc, null, 2);
  const blob = new Blob([content], { type: 'application/json' });

  // Trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizeFilename(name)}${GBOX_EXTENSION}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.info(`[Sandbox] Exported sandbox as ${name}${GBOX_EXTENSION}`);
}


/** Write each StoredAsset as a real binary file under `assets/<path>`. */
export function addAssetsFolderToZip(zip: JSZip, assets: StoredAsset[]): void {
  for (const a of assets) {
    zip.file(`assets/${a.path}`, base64ToBytes(a.base64));
  }
}

/**
 * Pure: the zip a standalone export downloads — the editor's project tree, the same one a save stores, with every empty
 * folder and every asset under `assets/`. Its package.json installs the gotti packages from their GitHub release
 * (`pinGottiRelease`: a project saved with `file:` links into a gotti checkout is pinned too). It runs under `gotti dev`
 * and `gotti playground` as downloaded.
 */
export function buildStandaloneZip(project: ProjectTree, assets: StoredAsset[]): JSZip {
  const zip = new JSZip();
  for (const [path, content] of Object.entries(project.files)) zip.file(path, path === 'package.json' ? pinGottiRelease(content) : content);
  for (const folder of project.folders) zip.folder(folder);
  addAssetsFolderToZip(zip, assets);
  return zip;
}

/**
 * Export sandbox as a standalone project (ZIP with package.json for local dev)
 */
export async function exportAsStandalone(project: ProjectTree, assets: StoredAsset[] = []): Promise<void> {
  const blob = await buildStandaloneZip(project, assets).generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'gotti-project.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.info('[Sandbox] Exported standalone project to gotti-project.zip');
}

/**
 * Legacy export function (for backwards compatibility)
 */
export { exportAsStandalone as exportToZip };

// ============================================================================
// Helper Functions
// ============================================================================

function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .substring(0, 50);
}
